import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid2";
import { Bank } from "@phosphor-icons/react/Bank";
import * as React from "react";

import { Plan } from "./plan";
import { contactV1Or2 } from "config/paths";

const plans = [
	{
		id: "business",
		name: "Business",
		popular: false,
		priceAnnual: 79,
		priceMonthly: 99,
		currency: "USD",
		description:
			"For individuals and small teams who need powerful mapping tools without complexity.",
		features: [
			"20 maps, 4 GB storage",
			"Unlimited public viewers",
			"GIS tools & symbology styling",
			"Custom branding (logo, colors, domain)",
			"Standard email support",
		],
	},
	{
		id: "enterprise",
		name: "Enterprise",
		popular: true,
		priceAnnual: 159,
		priceMonthly: 199,
		action: <Button variant="contained">Start free trial</Button>,
		currency: "USD",
		description:
			"For organizations that need secure sharing, collaboration, and workflow control.",
		features: [
			"40 maps, 8 GB+ storage",
			"100 free private viewers",
			"Group access control & audit logs",
			"Data sync via Dropbox",
			"Removable Mango branding",
			"Priority support & account manager",
			"Add editors, admins, storage and maps as needed",
			"All Business features",
		],
	},
	{
		id: "agency",
		name: "Agency",
		popular: false,
		priceAnnual: 319,
		priceMonthly: 399,
		currency: "USD",
		description:
			"For agencies managing multiple clients, high volumes of maps, or complex data projects.",
		features: [
			"100 maps, 16 GB+ storage",
			"Unlimited free private viewers",
			"Phone support & success manager",
			"Custom onboarding & education",
			"Access to professional services",
			"All Enterprise features included",
		],
	},
];

export function PlansTable() {
	const [isAnnual, setIsAnnual] = React.useState(true);

	const handleToggle = () => {
		setIsAnnual((prev) => !prev);
	};

	return (
		<Box
			sx={{
				bgcolor: "var(--mui-palette-background-level1)",
				py: { xs: "60px", sm: "120px" },
			}}>
			<Container maxWidth="lg">
				<Stack
					marginBottom={12}
					spacing={3}>
					<Stack
						spacing={2}
						sx={{ alignItems: "center" }}>
						<Typography
							sx={{ textAlign: "center" }}
							variant="h3">
							Sensibly priced web maps, no claptrap.
						</Typography>
						<Typography
							color="text.secondary"
							sx={{ textAlign: "center" }}
							variant="body1">
							Mango is perfect for mapping projects of any size and length.
						</Typography>
						<Stack
							direction="row"
							spacing={2}
							sx={{ alignItems: "center" }}>
							<Switch
								checked={isAnnual}
								onChange={handleToggle}
							/>
							<Typography variant="body1">Billed annually</Typography>
							<Chip
								color="success"
								label="20% OFF"
								size="small"
							/>
						</Stack>
					</Stack>
					<div>
						<Grid
							container
							spacing={3}>
							{plans.map((plan) => (
								<Grid
									key={plan.id}
									size={{
										md: 4,
										xs: 12,
									}}>
									<Plan
										{...plan}
										action={plan.action}
										isAnnual={isAnnual}
										price={isAnnual ? plan.priceAnnual : plan.priceMonthly}
									/>
								</Grid>
							))}
						</Grid>
					</div>
				</Stack>
			</Container>
			<Container maxWidth="md">
				<Stack>
					<Card sx={{ p: 2 }}>
						<CardContent sx={{ p: 2 }}>
							<Stack spacing={3}>
								<Typography
									align="center"
									variant="h5">
									Flexible Procurement and Payment
								</Typography>
								<Stack
									direction={{ sx: "column", md: "row" }}
									spacing={3}
									useFlexGap>
									<Box
										sx={{
											py: { xs: 2, md: 0 },
										}}
										width={{ xs: "100%", md: "50%" }}>
										<Stack
											direction="row"
											spacing={2}
											sx={{
												display: "flex",
												alignItems: "center",
												justifyContent: "flex-start",
												marginBottom: 2,
											}}>
											<Box
												component="img"
												height="42px"
												src="/new-mangomap-assets/payment-amex.svg"
											/>
											<Box
												component="img"
												src="/new-mangomap-assets/payment-visa.svg"
												width="56px"
											/>
											<Box
												component="img"
												height="36px"
												src="/new-mangomap-assets/payment-mastercard.svg"
											/>
										</Stack>
										<Typography
											color="text.secondary"
											component="p">
											We accept all major cards for self-serve checkout.
										</Typography>
									</Box>
									<Divider
										flexItem
										orientation="vertical"
										variant="middle"
									/>
									<Box
										sx={{
											py: { xs: 2, md: 0 },
										}}
										width={{ xs: "100%", md: "50%" }}>
										<Box width="48px">
											<Bank
												size="large"
												weight="duotone"
											/>
										</Box>
										<Typography
											color="text.secondary"
											component="p">
											Need a long-term solution? Get 30% off multi-year
											subscriptions with invoiced billing.
										</Typography>
									</Box>
								</Stack>
							</Stack>
						</CardContent>
						<CardActions sx={{ display: "flex", justifyContent: "flex-end" }}>
							<Button
								color="secondary"
								href={contactV1Or2}
								size="small"
								variant="contained">
								Talk to sales
							</Button>
						</CardActions>
					</Card>
				</Stack>
			</Container>
		</Box>
	);
}
